import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useMerchant, useUser } from "@auth";
import { useRequestTemplates } from "@flags";
import { cn } from "@utils";
import { useCoalescedInvalidation } from "../../../realtime/useCoalescedInvalidation";
import { channels } from "../../../realtime/channels";
import { useCustomerDetails } from "@api/customersController";
import { useCreateNote } from "@api/notesController";
import BatchTemplateRequestModal from "@areas/trade-accounts/flows/BatchTemplateRequestModal";
import SingleRequestDrawer from "@areas/trade-accounts/flows/SingleRequestDrawer";
import CapabilitiesHelp, { TOUR_SEEN_KEY, claimFirstView } from "../SmartNoteModal/CapabilitiesHelp";
import MentionTextarea from "../SmartNoteModal/MentionTextarea";
import useSmartNoteController from "../SmartNoteModal/useSmartNoteController";
import AccountHeader from "./AccountHeader";
import ConversationColumn, { DiscoverButton } from "./ConversationColumn";
import DetailTabs from "./DetailTabs";
import HistorySidebar from "./HistorySidebar";
import SmartNoteActionModals from "./SmartNoteActionModals";
import AddNoteModal from "./AddNoteModal";
import WhatsNewModal, { WHATS_NEW_SEEN_KEY } from "./WhatsNewModal";

const PROMPT_PLACEHOLDER =
  "You can add a note, send a request or type @ to notify a colleague";

function timeOfDayGreeting(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// The account-less landing: before an account is picked there's nothing to show the detail panel
// for, so this is a focused composer that lets the user choose one (@-search). Selecting an
// account swaps the whole dialog to the full account view below.
function AccountPicker({ controller: c, greeting }) {
  return (
    <DialogPanel
      transition
      className="w-[min(640px,95vw)] rounded-2xl bg-zinc-50 p-6 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
    >
      <div className="flex justify-end">
        <DiscoverButton onClick={c.openHelp} />
      </div>
      <h1 className="text-center text-2xl font-semibold text-zinc-900">{greeting}</h1>
      <p className="mt-2 text-center text-sm text-zinc-500">
        Pick an account to see its read, balance and activity.
      </p>
      <div className="mt-6 rounded-2xl border border-zinc-300 bg-white p-3 shadow-sm transition-[border-color,box-shadow] focus-within:border-zinc-400 focus-within:ring-4 focus-within:ring-zinc-100">
        <MentionTextarea
          key={c.editorKey}
          bare
          placeholder={PROMPT_PLACEHOLDER}
          onChange={c.handleDraftChange}
          onCustomerSelect={c.setSelectedCustomer}
          selectedCustomer={c.selectedCustomer}
          onSubmit={c.handleSubmit}
        />
        <div className="mt-3 flex items-center justify-end">
          <button
            type="button"
            onClick={c.handleSubmit}
            disabled={!c.canSubmit}
            aria-label="Send"
            className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary-400 text-white transition-colors hover:bg-primary-500 disabled:bg-primary-200"
          >
            <ArrowUpIcon aria-hidden="true" className="size-5" />
          </button>
        </div>
      </div>
    </DialogPanel>
  );
}

// Smallest either side may be squeezed to while dragging the divider.
const MIN_LEFT = 460;
const MIN_RIGHT = 380;
// Default split: the detail panel takes 45% of the modal, leaving the chat 55% (until dragged).
const RIGHT_FRACTION = 0.45;

// The draggable divider between the conversation column and the detail panel. Drives the right
// panel's width; the left column flexes to fill the rest. Keyboard-operable (arrows nudge, Shift =
// larger step) and exposed as a separator for assistive tech.
function ResizeHandle({ onDrag, onNudge }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panels"
      tabIndex={0}
      onPointerDown={onDrag}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 48 : 16;
        if (e.key === "ArrowLeft") {
          onNudge(step);
          e.preventDefault();
        } else if (e.key === "ArrowRight") {
          onNudge(-step);
          e.preventDefault();
        }
      }}
      className="group relative z-10 flex w-px shrink-0 cursor-col-resize items-center justify-center bg-zinc-200 transition-colors hover:bg-primary-300 focus:bg-primary-400 focus:outline-none"
    >
      {/* Widened invisible hit area so the 1px line is easy to grab. */}
      <span className="absolute inset-y-0 -left-2 -right-2" />
      <span className="pointer-events-none absolute h-9 w-1 rounded-full bg-zinc-300 opacity-0 transition-opacity group-hover:bg-primary-400 group-hover:opacity-100 group-focus:opacity-100" />
    </div>
  );
}

function AccountView({ controller: c, greeting }) {
  const composerRef = useRef(null);
  const rowRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useLocalStorage(
    "portal.smartNoteV2.historyOpen",
    false,
  );
  // null until the user drags the divider; until then we derive a 40% (detail) / 60% (chat) split
  // from the modal width so the default is proportional on any screen rather than a fixed pixel size.
  const [storedRightWidth, setRightWidth] = useLocalStorage(
    "portal.smartNoteV2.rightWidth.v2",
    null,
  );
  const defaultRight =
    typeof window !== "undefined"
      ? Math.round(window.innerWidth * 0.9 * RIGHT_FRACTION)
      : 560;
  const rightWidth = storedRightWidth ?? defaultRight;
  const customer = c.selectedCustomer;
  const { data: detail, isLoading } = useCustomerDetails(customer.id);
  // The read now lives on the Read tab, but the composer still holds its suggestions until it has landed.
  // Keyed by customer so a reveal reported for one account never unlocks another.
  const [readRevealedFor, setReadRevealedFor] = useState(null);
  const { merchant } = useMerchant();

  // Keep the Activity tab live. Any activity written for this customer — a Documents-tab send, an AI send,
  // or an externally-triggered one — pushes a realtime hint on the customer channel that invalidates the
  // feed, so the new row appears without a manual refresh (the write is async, so a one-shot invalidate on
  // the send wouldn't catch it). Mirrors the V1 CustomerContextPanel wiring, which V2 otherwise lacked.
  const customerChannel =
    merchant?.id && customer?.id ? channels.customer(merchant.id, customer.id) : null;
  useCoalescedInvalidation(customerChannel, ["customerActivities", customer.id]);

  const clampWidth = useCallback((width) => {
    const rowWidth = rowRef.current?.getBoundingClientRect().width ?? 0;
    const max = rowWidth ? Math.max(MIN_RIGHT, rowWidth - MIN_LEFT) : 1100;
    return Math.round(Math.min(max, Math.max(MIN_RIGHT, width)));
  }, []);

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const row = rowRef.current;
      if (!row) return;
      const move = (ev) => setRightWidth(clampWidth(row.getBoundingClientRect().right - ev.clientX));
      const stop = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", stop);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", stop);
    },
    [clampWidth, setRightWidth],
  );

  return (
    <DialogPanel
      transition
      className="relative flex h-[90vh] w-[90vw] max-w-none flex-col overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:scale-95"
    >
      <AccountHeader
        customer={customer}
        detail={detail}
        onClose={c.onClose}
        onAddNote={c.openNoteModal}
        onRequestPayment={c.handleRequestPayment}
        onBackToSearch={() => c.setSelectedCustomer(null)}
      />

      <div ref={rowRef} className="flex min-h-0 flex-1">
        {/* Drawer wrapper: always mounted so the rail can animate both ways — it clips to zero width when
            closed (so the chat column reflows smoothly) and the panel slides/fades as it opens. */}
        <div
          className={cn(
            "min-h-0 shrink-0 overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
            historyOpen ? "w-64" : "w-0",
          )}
        >
          <div
            className={cn(
              "h-full w-64 transition-all duration-300 ease-out motion-reduce:transition-none",
              historyOpen ? "translate-x-0 opacity-100" : "-translate-x-3 opacity-0",
            )}
          >
            <HistorySidebar
              open={historyOpen}
              customerId={customer.id}
              activeConversationId={c.activeConversationId}
              loadingConversationId={c.loadingConversationId}
              liveMessages={c.messages}
              onSelectConversation={c.loadConversation}
              onNewConversation={c.clearConversation}
              onCollapse={() => setHistoryOpen(false)}
            />
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1">
          <ConversationColumn
            controller={c}
            customer={customer}
            greeting={greeting}
            readRevealed={readRevealedFor === customer.id}
            historyOpen={historyOpen}
            onToggleHistory={() => setHistoryOpen((v) => !v)}
            composerRef={composerRef}
          />
        </div>

        <ResizeHandle
          onDrag={startResize}
          onNudge={(delta) => setRightWidth(clampWidth(rightWidth + delta))}
        />

        <div
          className="flex min-h-0 shrink-0 flex-col bg-white"
          style={{ width: rightWidth }}
        >
          <DetailTabs
            customer={customer}
            detail={detail}
            isLoading={isLoading}
            onReadReady={c.setReadReady}
            onReadRevealed={setReadRevealedFor}
            enterId={c.enterActivityId}
            activityPulse={c.activityPulse}
            onPreviewStep={(step, instalment) => c.setPreview({ step, instalment })}
            onOpenPaymentRequest={c.handleOpenPaymentRequest}
          />
        </div>
      </div>
    </DialogPanel>
  );
}

// V2 of the Smart Note / spacebar flow: a single unified account view — account header on top, the
// Ontik read + conversation on the left, and a tabbed Balance/Activity/Documents/Account panel on
// the right. Shares the conversation controller with V1, so suggestion accept/reject behave
// identically; only the layout differs. Gated by the smartNoteV2 flag in PortalTemplate.
export function SmartNoteModalV2({ isOpen, onClose, initialCustomer = null }) {
  const controller = useSmartNoteController({ isOpen, onClose, initialCustomer });
  const [helpOpen, setHelpOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  // First V2 open gets the what's-new intro; the capabilities tour waits for a later open so the
  // two never stack.
  useEffect(() => {
    if (!isOpen) return;
    if (claimFirstView(WHATS_NEW_SEEN_KEY)) setWhatsNewOpen(true);
    else if (claimFirstView(TOUR_SEEN_KEY)) setHelpOpen(true);
  }, [isOpen]);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const requestTemplatesEnabled = useRequestTemplates();
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const base = timeOfDayGreeting(new Date().getHours());
  const greeting = firstName ? `${base}, ${firstName}` : base;

  // The full account record (balance / overdue / contact), shared with AccountView via the query
  // cache. The request flow needs these fields to prefill amounts and recipients.
  const { data: detail } = useCustomerDetails(controller.selectedCustomer?.id);
  const requestCustomer = detail ?? controller.selectedCustomer;

  // "Request payment" opens the exact trade-account flow the accounts-table paper-airplane does:
  // the template picker when request templates are on, otherwise the single-request drawer.
  const [templateRequest, setTemplateRequest] = useState(null);
  const [singleRequest, setSingleRequest] = useState(null);
  const handleRequestPayment = () => {
    if (!requestCustomer) return;
    if (requestTemplatesEnabled) setTemplateRequest(requestCustomer);
    else setSingleRequest(requestCustomer);
  };
  const refreshAfterRequest = () => {
    const id = controller.selectedCustomer?.id;
    const code = requestCustomer?.accountCode;
    if (id) {
      queryClient.invalidateQueries({ queryKey: ["customer", id] });
      queryClient.invalidateQueries({ queryKey: ["customerActivities", id] });
    }
    if (code) queryClient.invalidateQueries({ queryKey: ["ledgerTransactions", code] });
  };

  // "Add note" opens a focused direct-note composer (not the AI chat). The save goes straight to
  // customers-service via useCreateNote — no intelligence-service, no conversation. On success we reuse the
  // controller's prependActivity purely for the UI reveal: it drops the row in and slides the detail panel
  // to Activity with the "squeeze in" entrance. Closing the composer on success lets that reveal play.
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const createNote = useCreateNote();
  const handleSaveNote = ({ content, mentions }) => {
    const customerId = controller.selectedCustomer?.id;
    if (!customerId) return;
    createNote.mutate(
      { customerId, content, mentionedUserIds: mentions.map((m) => m.id) },
      {
        onSuccess: (note) => {
          controller.prependActivity(customerId, {
            id: `optimistic-note-${note?.id ?? Date.now()}`,
            eventName: "NoteCreated",
            title: content,
            occurredAt: new Date().toISOString(),
            taggedUsers: mentions.map((m) => ({ userId: m.id, name: m.name })),
          });
          setNoteModalOpen(false);
        },
      },
    );
  };

  // Expose onClose + the request-payment / add-note openers to the children via the controller object.
  const c = {
    ...controller,
    onClose,
    handleRequestPayment,
    openNoteModal: () => setNoteModalOpen(true),
    openHelp: () => setHelpOpen(true),
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-zinc-900/60 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {c.selectedCustomer ? (
              <AccountView controller={c} greeting={greeting} />
            ) : (
              <AccountPicker controller={c} greeting={greeting} />
            )}
          </div>
        </div>
      </Dialog>

      <SmartNoteActionModals controller={c} />

      {/* The trade-account payment-request flow, same as the accounts-table paper-airplane. */}
      <BatchTemplateRequestModal
        isOpen={!!templateRequest}
        customerData={templateRequest}
        onClose={() => setTemplateRequest(null)}
        onComplete={refreshAfterRequest}
      />
      <SingleRequestDrawer
        isOpen={!!singleRequest}
        customer={singleRequest}
        elevated
        onClose={() => setSingleRequest(null)}
        onComplete={refreshAfterRequest}
      />

      <CapabilitiesHelp isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
      <WhatsNewModal isOpen={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />

      <AddNoteModal
        isOpen={noteModalOpen}
        customer={requestCustomer}
        onSave={handleSaveNote}
        onClose={() => setNoteModalOpen(false)}
        saving={createNote.isPending}
      />
    </>
  );
}

export default SmartNoteModalV2;
